// @ts-strict-ignore
import React from 'react';
import { MultiSelect } from '@usealloy/component-library';
import type { MultiSelectProps, Option, SelectedOption } from '@usealloy/component-library';
import { StyledAggregationSelectWrapper } from './styles';

type AggregationSelectProps =
  Omit<MultiSelectProps, 'onChange' | 'options' | 'selectedOptions' | 'isMulti'> & {
    multiSelectProps: Partial<MultiSelectProps>;
    options: Option[];
    value: string;
    onChange: (option: Option) => void;
  };

/** Finds an option by value in a flat or grouped list. */
function findOption(
  options: Option[],
  value: string,
): Option | undefined {
  for (const item of options) {
    if (item.value === value) return item;
    if (item.options) {
      const nested = item.options.find(
        (o: Option) => o.value === value,
      );
      if (nested) return nested;
    }
  }
  return undefined;
}

/** Dropdown for aggregation criteria. Single-select only; value is form field value. */
function AggregationSelect({
  multiSelectProps,
  options,
  value,
  onChange,
  ...props
}: AggregationSelectProps) {
  const selectedOption: Option | null = value
    ? findOption(options, value) ?? { value, label: value }
    : null;

  const mergedStyleFunctions: MultiSelectProps['customStyleFunctions'] = {
    ...multiSelectProps.customStyleFunctions,
    placeholder: () => ({
      ...multiSelectProps.customStyleFunctions?.placeholder?.({} as any, {} as any),
      color: 'var(--cl-text-code)',
      fontWeight: 'var(--cl-font-weight-bold)',
    }),
    singleValue: () => ({
      ...multiSelectProps.customStyleFunctions?.singleValue?.({} as any, {} as any),
      color: 'var(--cl-text-default)',
    }),
  };

  return (
    <StyledAggregationSelectWrapper>
      <MultiSelect
        {...multiSelectProps}
        {...props}
        customStyleFunctions={mergedStyleFunctions}
        options={options}
        selectedOptions={selectedOption}
        onChange={(raw: SelectedOption) => {
          const single = Array.isArray(raw) ? raw[0] : raw;
          if (single) onChange(single);
        }}
        isNaked
        isDisabled={false}
        isMulti={false}
      />
    </StyledAggregationSelectWrapper>
  );
}

export default AggregationSelect;
